import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useFamily } from './FamilyContext';
import {
  subscribeToHealthRecords,
  createHealthRecord as svcCreateHealthRecord,
  updateHealthRecord as svcUpdateHealthRecord,
  softDeleteHealthRecord as svcSoftDeleteHealthRecord,
} from '../services/healthService';
import {
  uploadHealthPhoto as svcUploadHealthPhoto,
  deleteHealthPhoto as svcDeleteHealthPhoto,
} from '../services/storageService';
import { invalidateHealthPhotoCache } from '../hooks/useHealthPhoto';

export const HealthContext = createContext(null);

/**
 * Provider de observaciones de salud del hogar activo.
 * Se monta dentro de AppShell, después de PetsProvider.
 *
 * Decisión: suscripción GLOBAL a todos los records del hogar. Para el beta
 * (≤10 pets × ~20 records cada uno = ~200 docs máximo) eso es despreciable.
 * Si en el futuro un hogar acumula miles de records, evaluamos lazy
 * per-pet subscriptions.
 *
 * `records` se sortea desc por `occurredAt`. El caller filtra por pet
 * con `getRecordsForPet`.
 */
export const HealthProvider = ({ children }) => {
  const { profile } = useAuth();
  const { family } = useFamily();
  const familyId = family?.id ?? null;

  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!familyId) {
      setRecords([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const unsubscribe = subscribeToHealthRecords(
      familyId,
      (next) => {
        const sorted = [...next].sort((a, b) => {
          const aTime = a.occurredAt?.toMillis?.() ?? 0;
          const bTime = b.occurredAt?.toMillis?.() ?? 0;
          return bTime - aTime; // desc: más recientes primero
        });
        setRecords(sorted);
        setStatus('ready');
        setError(null);
      },
      (err) => {
        setError(err);
        setStatus('error');
      }
    );

    return unsubscribe;
  }, [familyId]);

  // ===== Mutations =====

  const createHealthRecord = useCallback(
    async (input) => {
      if (!profile?.uid || !familyId) throw new Error('Sin sesión o sin hogar');
      return svcCreateHealthRecord({ ...input, uid: profile.uid, familyId });
    },
    [profile?.uid, familyId]
  );

  const updateHealthRecord = useCallback(
    async (recordId, updates) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcUpdateHealthRecord({ uid: profile.uid, recordId, updates });
    },
    [profile?.uid]
  );

  const softDeleteHealthRecord = useCallback(
    async (recordId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSoftDeleteHealthRecord({ uid: profile.uid, recordId });
    },
    [profile?.uid]
  );

  /**
   * Sube la foto al worker (Supabase) + actualiza `photoExt` en Firestore.
   * Dos pasos no-atómicos: si el update falla post-upload, queda el objeto
   * en Supabase huérfano. El próximo upload sobreescribe (path determinístico).
   *
   * Requiere recordId existente — el caller crea primero el record, después
   * sube la foto.
   */
  const uploadHealthRecordPhoto = useCallback(
    async (record, file) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      if (!record?.id || !record?.petId) {
        throw new Error('record con id + petId requeridos');
      }
      const { ext } = await svcUploadHealthPhoto({
        petId: record.petId,
        recordId: record.id,
        file,
      });
      await svcUpdateHealthRecord({
        uid: profile.uid,
        recordId: record.id,
        updates: { photoExt: ext },
      });
      invalidateHealthPhotoCache(record.id);
      return { ext };
    },
    [profile?.uid]
  );

  const deleteHealthRecordPhoto = useCallback(
    async (record) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      if (!record?.id || !record?.petId) {
        throw new Error('record con id + petId requeridos');
      }
      await svcDeleteHealthPhoto({ petId: record.petId, recordId: record.id });
      await svcUpdateHealthRecord({
        uid: profile.uid,
        recordId: record.id,
        updates: { photoExt: null },
      });
      invalidateHealthPhotoCache(record.id);
    },
    [profile?.uid]
  );

  // ===== Selectors =====

  const getRecordById = useCallback(
    (recordId) => records.find((r) => r.id === recordId) ?? null,
    [records]
  );

  /**
   * Records de UNA pet, ordenados desc por occurredAt (ya viene ordenado global).
   * Útil para PetHealthPage.
   */
  const getRecordsForPet = useCallback(
    (petId) => records.filter((r) => r.petId === petId),
    [records]
  );

  /**
   * Mapa { petId → lastOccurredAtMs } para la HealthPage (lista de mascotas
   * con badge "última observación: hace X días").
   */
  const lastRecordByPet = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const ms = r.occurredAt?.toMillis?.() ?? 0;
      if (!map.has(r.petId) || ms > map.get(r.petId)) {
        map.set(r.petId, ms);
      }
    }
    return map;
  }, [records]);

  const value = useMemo(
    () => ({
      records,
      status,
      error,
      isLoading: status === 'loading',
      isEmpty: status === 'ready' && records.length === 0,
      getRecordById,
      getRecordsForPet,
      lastRecordByPet,
      createHealthRecord,
      updateHealthRecord,
      softDeleteHealthRecord,
      uploadHealthRecordPhoto,
      deleteHealthRecordPhoto,
    }),
    [
      records,
      status,
      error,
      getRecordById,
      getRecordsForPet,
      lastRecordByPet,
      createHealthRecord,
      updateHealthRecord,
      softDeleteHealthRecord,
      uploadHealthRecordPhoto,
      deleteHealthRecordPhoto,
    ]
  );

  return <HealthContext.Provider value={value}>{children}</HealthContext.Provider>;
};

export const useHealth = () => {
  const ctx = useContext(HealthContext);
  if (!ctx) {
    throw new Error('useHealth must be used within a HealthProvider');
  }
  return ctx;
};
